import { useEffect, useState, type ReactNode } from "react";
import {
  Heart,
  ShoppingBag,
  Mail,
  CheckCircle,
  XCircle,
  Search,
  Menu,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StyleguideGate } from "@/components/StyleguideGate";
import { Seo } from "@/components/Seo";

interface TokenGroupProps {
  title: string;
  tokens: Array<{ name: string; description?: string }>;
}

function useCssVar(name: string): string {
  const [value, setValue] = useState("");
  useEffect(() => {
    const computed = getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
    setValue(computed);
  }, [name]);
  return value;
}

function Swatch({ token, description }: { token: string; description?: string }) {
  const value = useCssVar(`--${token}`);
  return (
    <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
      <div
        className="h-14 w-14 shrink-0 rounded-lg border border-border"
        style={{ backgroundColor: `hsl(${value})` }}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="font-mono text-sm font-semibold">--{token}</p>
        <p className="truncate font-mono text-xs text-muted-foreground">
          hsl({value || "…"})
        </p>
        {description && (
          <p className="mt-1 text-xs text-muted-foreground font-body">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

function TokenGroup({ title, tokens }: TokenGroupProps) {
  return (
    <div>
      <h3 className="font-heading text-xl font-bold">{title}</h3>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {tokens.map((t) => (
          <Swatch key={t.name} token={t.name} description={t.description} />
        ))}
      </div>
    </div>
  );
}

function Section({
  id,
  eyebrow,
  title,
  children,
}: {
  id: string;
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="py-8 md:py-12">
      <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
        {eyebrow}
      </p>
      <h2 className="mt-2 font-heading text-2xl font-bold md:text-3xl">{title}</h2>
      <div className="mt-8">{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-3 rounded-xl border border-border bg-card p-5 md:grid-cols-[10rem_1fr] md:items-center">
      <p className="font-mono text-xs text-muted-foreground">{label}</p>
      <div>{children}</div>
    </div>
  );
}

const BRAND_TOKENS = [
  { name: "primary", description: "Brand-grønn. CTA, aktiv nav, logger." },
  { name: "primary-foreground", description: "Tekst/ikon på primary bakgrunn." },
  { name: "accent", description: "Sekundær brand-farge." },
  { name: "accent-foreground", description: "Tekst/ikon på accent bakgrunn." },
  { name: "warm-cream", description: "Varm bakgrunn for kort/hero." },
  { name: "warm-sand", description: "Varm sekundær-bakgrunn." },
];

const NATURE_TOKENS = [
  { name: "nature-deep", description: "Dyp skog. Mørkere flater." },
  { name: "nature-mid", description: "Mellomgrønn. Primary-aliaset." },
  { name: "nature-light", description: "Lys grønn. Accents." },
  { name: "nature-pale", description: "Svært lys grønn. Bakgrunner." },
];

const NEUTRAL_TOKENS = [
  { name: "background", description: "Sidens hovedbakgrunn." },
  { name: "foreground", description: "Hovedtekstfarge." },
  { name: "card", description: "Kort/paneler." },
  { name: "muted", description: "Dempet bakgrunn." },
  { name: "muted-foreground", description: "Dempet tekstfarge." },
  { name: "secondary", description: "Sekundær-knapper og fremheving." },
  { name: "border", description: "Border på kort, inputs." },
  { name: "destructive", description: "Feil / slett." },
];

const ICON_EXAMPLES = [
  { Icon: Heart, label: "Heart" },
  { Icon: ShoppingBag, label: "ShoppingBag" },
  { Icon: Mail, label: "Mail" },
  { Icon: CheckCircle, label: "CheckCircle" },
  { Icon: XCircle, label: "XCircle" },
  { Icon: Search, label: "Search" },
  { Icon: Menu, label: "Menu" },
  { Icon: MapPin, label: "MapPin" },
];

const HERO_IMAGE =
  "https://rvpirrrwpasphlirukef.supabase.co/storage/v1/object/public/brand-images/cbab4041-35cb-43ab-a927-605a6ef2fc6d/logo_onboarding.png";

const StyleguideContent = () => {
  return (
    <main className="min-h-screen bg-background pb-20">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto max-w-5xl px-4 py-10">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
            Designsystem
          </p>
          <h1 className="mt-2 font-heading text-3xl font-bold md:text-4xl">
            Håp Naturlab · Styleguide
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground font-body leading-relaxed">
            Live-referanse for farger, typografi, spacing og komponenter. Alle
            verdier leses direkte fra CSS-variablene som er satt i produksjon.
          </p>
          <nav className="mt-6 flex flex-wrap gap-2 text-sm font-body">
            {[
              ["Farger", "colors"],
              ["Typografi", "typography"],
              ["Spacing", "spacing"],
              ["Layout", "layout"],
              ["Komponenter", "components"],
              ["Ikoner", "icons"],
              ["Bilder", "images"],
            ].map(([label, href]) => (
              <a
                key={href}
                href={`#${href}`}
                className="rounded-full border border-border px-4 py-1.5 text-muted-foreground transition-colors hover:border-primary hover:text-primary"
              >
                {label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <div className="container mx-auto max-w-5xl px-4">
        <Section id="colors" eyebrow="01 · Farger" title="Fargepalett">
          <p className="max-w-2xl text-base text-muted-foreground font-body leading-relaxed">
            Farger defineres som CSS-variabler i HSL-format og mappes til
            Tailwind-klasser i{" "}
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              tailwind.config.ts
            </code>
            . Swatch-ene nedenfor leser live-verdiene fra roten av DOM-et.
          </p>
          <div className="mt-10 space-y-12">
            <TokenGroup title="Brand" tokens={BRAND_TOKENS} />
            <TokenGroup title="Nature-skala" tokens={NATURE_TOKENS} />
            <TokenGroup title="Nøytraler / UI" tokens={NEUTRAL_TOKENS} />
          </div>
        </Section>

        <Section id="typography" eyebrow="02 · Typografi" title="Skriftsystem">
          <p className="max-w-2xl text-base text-muted-foreground font-body leading-relaxed">
            Overskrifter bruker Playfair Display (
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              font-heading
            </code>
            ), brødtekst bruker Open Sans (
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              font-body
            </code>
            ). Hierarki og Tailwind-klasser er faste — kun font-valg tilpasses
            per kunde.
          </p>

          <div className="mt-10 space-y-4">
            <Row label="h1">
              <p className="text-3xl md:text-4xl font-heading font-bold">
                Hver krukke gir håp
              </p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                text-3xl md:text-4xl font-heading font-bold
              </p>
            </Row>
            <Row label="h2">
              <p className="text-2xl md:text-3xl font-heading font-bold">
                Et fellesskap drevet av hjertevarme
              </p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                text-2xl md:text-3xl font-heading font-bold
              </p>
            </Row>
            <Row label="h3">
              <p className="text-xl font-heading font-bold">Naturlig hudpleie</p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                text-xl font-heading font-bold
              </p>
            </Row>
            <Row label="h4">
              <p className="text-lg font-heading font-bold">Skånsom håndkrem</p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                text-lg font-heading font-bold
              </p>
            </Row>
            <Row label="body">
              <p className="text-base font-body text-muted-foreground leading-relaxed">
                Velkommen til et fellesskap drevet av ren frivillighet og et
                brennende ønske om å hjelpe. Håp Naturlab er ikke som andre
                hudpleiemerker — vi har ingen ansatte og ingen personlig profitt.
              </p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                text-base font-body text-muted-foreground leading-relaxed
              </p>
            </Row>
            <Row label="label">
              <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
                Fast giver
              </p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                text-xs uppercase tracking-[0.2em] text-primary font-semibold
              </p>
            </Row>
          </div>
        </Section>

        <Section id="spacing" eyebrow="03 · Spacing" title="Seksjons-spacing">
          <p className="max-w-2xl text-base text-muted-foreground font-body leading-relaxed">
            Hovedseksjoner puster med{" "}
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              py-16 md:py-20
            </code>
            , under-seksjoner med{" "}
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              py-8 md:py-12
            </code>
            .
          </p>
          <div className="mt-8 space-y-4">
            <Row label="py-16 md:py-20">
              <div className="rounded-lg bg-primary/10">
                <div className="py-16 text-center font-mono text-xs text-primary md:py-20">
                  Hovedseksjon
                </div>
              </div>
            </Row>
            <Row label="py-8 md:py-12">
              <div className="rounded-lg bg-accent/10">
                <div className="py-8 text-center font-mono text-xs text-accent md:py-12">
                  Underseksjon
                </div>
              </div>
            </Row>
          </div>
        </Section>

        <Section id="layout" eyebrow="04 · Layout" title="Containere og grid">
          <p className="max-w-2xl text-base text-muted-foreground font-body leading-relaxed">
            Bruk{" "}
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              container mx-auto px-4
            </code>{" "}
            som basis og kombiner med en{" "}
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              max-w-*
            </code>
            . Grid:{" "}
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3
            </code>
            .
          </p>
          <div className="mt-8 space-y-4">
            {["max-w-3xl", "max-w-4xl", "max-w-5xl"].map((cls) => (
              <div key={cls} className="rounded-xl border border-border bg-card p-5">
                <p className="font-mono text-xs text-muted-foreground">{cls}</p>
                <div
                  className={`mt-3 ${cls} rounded-lg bg-primary/10 py-3 text-center font-body text-sm text-primary`}
                >
                  {cls}
                </div>
              </div>
            ))}
            <Row label="3-kolonne grid">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                {[1, 2, 3].map((n) => (
                  <div
                    key={n}
                    className="rounded-lg bg-secondary py-6 text-center font-body text-sm"
                  >
                    Kolonne {n}
                  </div>
                ))}
              </div>
            </Row>
          </div>
        </Section>

        <Section id="components" eyebrow="05 · Komponenter" title="shadcn/ui">
          <p className="max-w-2xl text-base text-muted-foreground font-body leading-relaxed">
            Standardkomponenter fra shadcn/ui med prosjekt-theming via CSS-variablene
            over.
          </p>

          <div className="mt-10 space-y-10">
            <div>
              <h3 className="font-heading text-xl font-bold">Button</h3>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button>Default</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="destructive">Destructive</Button>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button size="sm">Small</Button>
                <Button>Default</Button>
                <Button size="lg">Large</Button>
              </div>
            </div>

            <div>
              <h3 className="font-heading text-xl font-bold">Input / Textarea</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="sg-name"
                    className="mb-1.5 block text-sm font-semibold font-body"
                  >
                    Navn
                  </label>
                  <Input id="sg-name" placeholder="Ditt navn" />
                </div>
                <div>
                  <label
                    htmlFor="sg-email"
                    className="mb-1.5 block text-sm font-semibold font-body"
                  >
                    E-post
                  </label>
                  <Input id="sg-email" type="email" placeholder="din@epost.no" />
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="sg-msg"
                    className="mb-1.5 block text-sm font-semibold font-body"
                  >
                    Melding
                  </label>
                  <Textarea id="sg-msg" rows={4} placeholder="Skriv her…" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-heading text-xl font-bold">Badge</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                <Badge>Nyhet</Badge>
                <Badge variant="secondary">Populær</Badge>
                <Badge variant="outline">Utsolgt</Badge>
                <Badge variant="destructive">Avvist</Badge>
              </div>
            </div>

            <div>
              <h3 className="font-heading text-xl font-bold">Card</h3>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading">Produktkort</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      Standard kort med tittel og innhold. Brukes for produkter,
                      team, hendelser.
                    </p>
                    <Button variant="outline" size="sm">
                      Les mer
                    </Button>
                  </CardContent>
                </Card>
                <Card className="bg-primary/5">
                  <CardHeader>
                    <CardTitle className="font-heading">Fremhevet kort</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      Fremhevet variant med primary/5 bakgrunn for CTA-seksjoner.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </Section>

        <Section id="icons" eyebrow="06 · Ikoner" title="Lucide React">
          <p className="max-w-2xl text-base text-muted-foreground font-body leading-relaxed">
            Standardstørrelser er{" "}
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              size={16}
            </code>{" "}
            (inline) og{" "}
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              size={20}
            </code>{" "}
            (default). Farge arves fra tekst med{" "}
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">
              text-primary
            </code>{" "}
            og venner.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {ICON_EXAMPLES.map(({ Icon, label }) => (
              <div
                key={label}
                className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-5 text-primary"
              >
                <Icon size={20} aria-hidden="true" />
                <p className="font-mono text-xs text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section id="images" eyebrow="07 · Bilder" title="Bildestil">
          <div className="space-y-8">
            <Row label="Hero 16:7">
              <div className="relative aspect-[16/7] overflow-hidden rounded-2xl">
                <img
                  src={HERO_IMAGE}
                  alt=""
                  aria-hidden="true"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50" />
                <div className="absolute inset-0 flex items-end p-6">
                  <p className="font-heading text-2xl font-bold text-white">
                    Hero med gradient-overlay
                  </p>
                </div>
              </div>
              <p className="mt-2 font-mono text-xs text-muted-foreground">
                aspect-[16/7] md:aspect-[16/7] + bg-gradient-to-t from-black/50
              </p>
            </Row>

            <Row label="Thumbnail">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[1, 2, 3, 4].map((n) => (
                  <div
                    key={n}
                    className="aspect-square overflow-hidden rounded-lg bg-secondary/50"
                  >
                    <img
                      src={HERO_IMAGE}
                      alt=""
                      aria-hidden="true"
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <p className="mt-2 font-mono text-xs text-muted-foreground">
                aspect-square rounded-lg object-cover
              </p>
            </Row>

            <Row label="Inline">
              <div className="overflow-hidden rounded-xl shadow-md">
                <img
                  src={HERO_IMAGE}
                  alt=""
                  aria-hidden="true"
                  className="h-48 w-full object-cover"
                />
              </div>
              <p className="mt-2 font-mono text-xs text-muted-foreground">
                rounded-xl shadow-md object-cover
              </p>
            </Row>
          </div>
        </Section>
      </div>
    </main>
  );
};

const Styleguide = () => (
  <StyleguideGate>
    <Seo
      title="Styleguide – Håp Naturlab"
      description="Intern designsystem-referanse for Håp Naturlab."
      noIndex
    />
    <StyleguideContent />
  </StyleguideGate>
);

export default Styleguide;
